/**
 * File: projectContextRequirements.ts
 * Description: Shared checks for mandatory project context documents.
 * Author: GitHub Copilot
 * Created: 2026-06-23
 * Last Modified: 2026-06-23
 */

import {
  ProjectContextResponse,
  ProjectContextUploadedDocumentType,
} from '../types/project';

export const BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_TITLE =
  'Business Requirements Document required';
export const BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_MESSAGE =
  'Upload an Business Requirements Document before continuing to other sections.';
export const BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_TOOLTIP =
  'Upload a Business Requirements Document to continue.';

interface UploadedDocumentCandidate {
  type: string;
  status?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Determines whether project context contains an uploaded Business Requirements
 * Document. Knowledge-base references and other document types do not satisfy
 * this mandatory workflow requirement.
 * @param contextItems - Project context items returned by the API
 * @return True when an uploaded BRD exists in project context
 */
export const hasBusinessRequirementsDocument = (
  contextItems: ProjectContextResponse[],
): boolean =>
  contextItems.some(
    (item) =>
      item.type === 'ProjectContextUploadedDocument' &&
      item.document_type ===
        ProjectContextUploadedDocumentType.BUSINESS_REQUIREMENTS_DOCUMENT,
  );

/**
 * Determines whether local uploaded-file state contains a completed Business
 * Requirements Document. This keeps the continue action enabled immediately
 * after upload, even before the next full context reload completes.
 * @param uploadedDocuments - Local uploaded document candidates
 * @return True when a completed uploaded BRD exists locally
 */
export const hasCompletedBusinessRequirementsUpload = (
  uploadedDocuments: UploadedDocumentCandidate[],
): boolean =>
  uploadedDocuments.some((document) => {
    if (document.status !== 'completed') {
      return false;
    }

    const documentType =
      document.metadata?.document_type ?? document.type;

    return (
      documentType ===
      ProjectContextUploadedDocumentType.BUSINESS_REQUIREMENTS_DOCUMENT
    );
  });